import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";
import { isAdminHost } from "@/lib/host";
import HomePage from "@/pages/HomePage";
import ExplorerPage from "@/pages/ExplorerPage";
import CeSoirPage from "@/pages/CeSoirPage";
import EvenementsPage from "@/pages/EvenementsPage";
import HebergementsPage from "@/pages/HebergementsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import EstablishmentDetailPage from "@/pages/EstablishmentDetailPage";
import PublierPage from "@/pages/PublierPage";
import MobilePage from "@/pages/MobilePage";
import ProfilPage from "@/pages/ProfilPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ProDashboardPage from "@/pages/ProDashboardPage";
import PaymentReturnPage from "@/pages/PaymentReturnPage";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FavorisPage from "@/pages/FavorisPage";
import MaintenancePage from "@/pages/MaintenancePage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Enregistre une visite anonyme à chaque navigation sur le site public (hors /admin et /pro)
function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

// Mode maintenance : masque le site public quand actif. /admin et /connexion restent accessibles.
// Revérifié à chaque navigation + toutes les 15 s pendant la maintenance (réactivation immédiate).
// Ne s'applique JAMAIS à l'espace admin (admin.gospot.ca n'emprunte pas ce portail).
function MaintenanceGate({ children }) {
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = () => {
      api
        .get("/settings/maintenance")
        .then((r) => {
          if (!cancelled) setActive(!!r.data?.active);
        })
        .catch(() => {});
    };
    check();
    const interval = setInterval(check, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pathname]);

  const exempt = pathname.startsWith("/admin") || pathname === "/connexion" || pathname === "/reinitialiser-mot-de-passe";
  if (active && !exempt) return <MaintenancePage />;
  return children;
}

// Le panneau Admin vit en local sur /admin (même domaine que le site public).
// Le portail de maintenance exempte TOUJOURS /admin/* — vérifié avant tout masquage.

// Site public (gospot.ca) — suivi des visites, header public et portail de maintenance
const PublicSite = () => (
  <MaintenanceGate>
    <PageTracker />
    <Header />
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
      <Route path="/explorer" element={<ExplorerPage />} />
      <Route path="/ce-soir" element={<CeSoirPage />} />
      <Route path="/evenements" element={<EvenementsPage />} />
      <Route path="/hebergements" element={<HebergementsPage />} />
      <Route path="/publier" element={<PublierPage />} />
      <Route path="/mobile" element={<MobilePage />} />
      <Route path="/profil" element={<ProfilPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/connexion" element={<AdminLoginPage />} />
      <Route path="/pro" element={<Navigate to="/pro/etablissement" replace />} />
      <Route path="/pro/:section" element={<ProDashboardPage />} />
      <Route path="/paiement/succes" element={<PaymentReturnPage />} />
      <Route path="/favoris" element={<FavorisPage />} />
      <Route path="/etablissement/:slug" element={<EstablishmentDetailPage />} />
      <Route path="/evenement/:slug" element={<EventDetailPage />} />
    </Routes>
  </MaintenanceGate>
);

// Espace d'administration (prêt pour un futur admin.gospot.ca) — connexion dédiée, réservée au rôle Administrateur.
// Jamais masqué par le mode maintenance, aucun suivi de visite, aucun header public.
const AdminSpace = () => (
  <Routes>
    <Route path="/" element={<AdminLoginPage />} />
    <Route path="/panneau" element={<AdminPage />} />
    <Route path="/profil" element={<ProfilPage />} />
    <Route path="/admin" element={<Navigate to="/panneau" replace />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, duration: 1.05 });
    return () => lenis.destroy();
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="min-h-screen bg-[#F8FAFC]">
            {isAdminHost() ? <AdminSpace /> : <PublicSite />}
            <Toaster position="top-center" richColors />
          </div>
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
